if (!customElements.get('okendo-testimonials')) {
  customElements.define(
    'okendo-testimonials',
    class OkendoTestimonials extends HTMLElement {
      connectedCallback() {
        this.slideshow = this.querySelector('.testimonials-slider');
        this.template = this.querySelector('template');
        if (!this.slideshow || !this.template) return;

        this.onReadMore = this.onReadMore.bind(this);

        if (window.theme && theme.Slideshow) {
          this.observe();
        } else {
          document.addEventListener('page:loaded', () => this.observe(), { once: true });
        }
      }

      disconnectedCallback() {
        clearTimeout(this.timeout);
        if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
        if (this.flickity && typeof this.flickity.destroy === 'function') {
          this.flickity.destroy();
        }
      }

      observe() {
        theme.initWhenVisible({
          element: this,
          callback: this.init.bind(this),
          threshold: 600
        });
      }

      async init() {
        let reviews = [];
        try {
          const response = await fetch(
            `https://api.okendo.io/v1/stores/${this.dataset.sourceId}/reviews?limit=${this.dataset.count}&orderBy=date%20desc`
          );
          if (!response.ok) throw new Error(`Okendo request failed: ${response.status}`);
          const json = await response.json();
          reviews = json.reviews || [];
        } catch (error) {
          console.error(error);
        }

        if (!reviews.length) {
          this.closest('.testimonials-section')?.classList.add('hide');
          return;
        }

        this.renderReviews(reviews);
        this.initSlides(reviews.length);
        this.checkOverflow();

        this.slideshow.addEventListener('click', this.onReadMore);
        this.resizeHandler = theme.utils.debounce(300, this.checkOverflow.bind(this));
        window.addEventListener('resize', this.resizeHandler);
      }

      renderReviews(reviews) {
        const reviewNode = this.template.content.querySelector('.testimonials-slide');

        reviews.forEach((review, index) => {
          const blockElement = reviewNode.cloneNode(true);
          const stars = blockElement.querySelector('.testimonial-stars');
          stars.textContent = '★'.repeat(review.rating);
          stars.setAttribute('aria-label', `Rated ${review.rating} out of 5`);
          blockElement.querySelector('.review-title').textContent = review.title;
          const body = blockElement.querySelector('.testimonial-body');
          body.textContent = review.body;
          body.id = `testimonial-body-${this.dataset.sectionId}-${index}`;
          blockElement.querySelector('.testimonial-read-more').setAttribute('aria-controls', body.id);
          blockElement.querySelector('cite').textContent = review.reviewer.displayName;
          blockElement.setAttribute('data-index', index);
          if (!review.reviewer.isVerified) {
            blockElement.querySelector('.testimonials__info').remove();
          }
          this.slideshow.appendChild(blockElement);
        });

        this.template.remove();
      }

      initSlides(count) {
        this.flickity = new theme.Slideshow(this.slideshow, {
          adaptiveHeight: true,
          avoidReflow: true,
          pageDots: true,
          prevNextButtons: false,
          // Do not wrap when only a few reviews
          wrapAround: count > 3
        });

        // Autoscroll to next slide on load to indicate more reviews
        if (count > 2) {
          this.timeout = setTimeout(() => this.flickity.goToSlide(1), 1000);
        }
      }

      checkOverflow() {
        this.slideshow.querySelectorAll('.testimonial-body:not(.is-expanded)').forEach((body) => {
          const btn = body.nextElementSibling;
          const overflows = body.scrollHeight > body.clientHeight + 1;
          body.classList.toggle('is-truncated', overflows);
          btn.classList.toggle('hide', !overflows);
        });
        this.resizeSlider();
      }

      onReadMore(evt) {
        const btn = evt.target.closest('.testimonial-read-more');
        if (!btn) return;

        const body = document.getElementById(btn.getAttribute('aria-controls'));
        if (!body) return;

        const expand = btn.getAttribute('aria-expanded') !== 'true';
        body.classList.toggle('is-expanded', expand);
        body.classList.toggle('is-truncated', !expand);
        btn.setAttribute('aria-expanded', expand);
        btn.textContent = expand ? 'Read less' : 'Read more';

        // adaptiveHeight needs a resize so the slider grows/shrinks with the slide
        this.resizeSlider();
      }

      resizeSlider() {
        if (this.flickity && typeof this.flickity.resize === 'function') {
          this.flickity.resize();
        }
      }
    }
  );
}
